import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardBody, CardFooter, CardHeader, Col, Row } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import * as echarts from 'echarts/core';
import getChartColorsArray from '../../../../../Components/Common/ChartsDynamicColor';
import { useDispatch, useSelector } from 'react-redux';
import { getDataReportSalinity } from '../../../../../slices/thunks';
import config, { api } from '../../../../../config';
import moment from 'moment';
import OPlaceholder from '../../../../../Components/OElements/OPlaceholder';
import { getEnviromentalParameterData, getMicroalgaeNociveHistoricalData } from '../../../../../helpers/data_sped/backend_helper';
import EChartsBarChart from '../../../../../Components/Common/Charts/ECharts/EChartsBarChart';

const MicroalgaeConcentrationSummary = ({ title, um, code_siep, h100 = true, height = 150, showHeader = true, showFooter = true }) => {
    const dispatch = useDispatch();
    const [data, setData] = useState(null);
    const [dataInterval, setDataInterval] = useState('7d');
    const [chartCategories, setChartCategories] = useState([]);
    const chartRef = useRef(null);

    const allowedDataIntervals = ['1m', '15d', '7d'];

    const dataColors = '["--vz-primary", "--vz-secondary"]';
    const revenueExpensesChartsColors = getChartColorsArray(dataColors);

    // Acceder directamente al slice del estado
    const { dataProvider, isLoading, isSuccess, isError } = useSelector((state) => state.SalinityProvider || {});

    useEffect(() => {
        if(!['', null].includes(api.REACT_APP_SPED_ORIGINAL_API_URL)){
            const loadData = async () => {
                const response = await getMicroalgaeNociveHistoricalData(
                    code_siep,
                    null,
                    null,
                    dataInterval,
                    true
                );
                setData(response || [])
            }
            loadData();
        }else{
            // Llama a la acción para obtener datos
            const requestData = {
                username: config.user_providers.USER,
                password: config.user_providers.PASS,
                service: 'innovex',
                interval: dataInterval,
                siep: code_siep,
                variable: 'salinity',
            };

            if (code_siep && code_siep !== 'all') {
                dispatch(getDataReportSalinity(requestData));
            }
        }
    }, [dataInterval, code_siep, dispatch]);



    useEffect(() => {
        if (data?.time) {
            const intervalFormats = {
                '24h': 'HH:00',
                '7d': 'DD-MMM',
                '15d': 'DD-MMM',
                '1m': 'DD-MMM',
                'ciclo': 'DD-MM-YYYY',
            };

            const processData = (timeData, type) => {
                const format = intervalFormats[type] || 'YYYY-MM-DD';
                return Object.values(timeData).map(time => moment(time).format(format));
            };

            setChartCategories(processData(data.time, dataInterval));
        }
    }, [data, dataInterval, code_siep]);

    const generateDateRange = (start, end) => {
        if (!start || !end) return [];
        const startDate = new Date(`${start}T00:00:00`);
        const endDate = new Date(`${end}T00:00:00`);
        const dates = [];

        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            dates.push(`${day}-${month}-${year}`);
        }

        return dates;
    };

    const prepareSeriesData = (rawData, dateRange) => {
        if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
            return { date: [], seriesData: [] };
        }

        const dateSet = new Set();
        rawData.forEach(item => {
            dateSet.add(item.fecha);
        });

        const dates = generateDateRange(dateRange[0], dateRange[1]);

        const mapSeries = {};

        rawData.forEach(item => {
            const nombre = item.microalga__nombre;
            const fechaStr = item.fecha;
            const total = item.total;
            const maximo = item.maximo;

            if (!mapSeries[nombre]) mapSeries[nombre] = {};
            mapSeries[nombre][fechaStr] = {
                value: maximo,
                fecha: fechaStr,
                limits: {
                    normal: item.limiteNormal,
                    bajo: item.limiteBajo,
                    medio: item.limiteMedio,
                    alto: item.limiteAlto
                }
            };
        });

        const seriesData = Object.entries(mapSeries).map(([nombre, valoresPorFecha]) => {
        const data = dates.map(
            fecha =>
                valoresPorFecha[fecha] ?? {
                    value: 0,
                    fecha,
                    limits: null
                }
            );
            return { nombre, data };
        });

        return { date: dates, seriesData };
    };

    const generateSeriesForChart = (seriesData) => {
        if (!seriesData || !Array.isArray(seriesData)) return [];

        return seriesData.map(s => ({
            name: s.nombre,
            type: 'bar',
            stack: 'total',
            data: s.data
        }));
    };

    const Charts = ({ minHeight, maxHeight }) => {
        const dataChart = prepareSeriesData(data?.series, data?.rangoFechas);
        const seriesForChart = generateSeriesForChart(dataChart.seriesData);

        const options = {
            title: {
                text: seriesForChart?.length ? '' : 'Sin datos',
                left: 'center',
                top: 'middle',
                textStyle: {
                fontSize: 12,
                color: '#999'
                }
            },
            tooltip: {
                trigger: 'item',
                confine: true,
                formatter: (params) => {
                    const { marker, seriesName, data } = params;

                    if (!data) return '';

                    const limits = data.limits;

                    return `
                        <div style="margin-bottom: 6px;">
                            ${marker}
                            <strong>${seriesName}</strong><br/>
                            Fecha: ${data.fecha}<br/>
                            Concentración Máxima: <strong>${data.value}</strong>
                        </div>

                        ${
                            limits
                            ? `
                            <div style="margin-top: 8px; font-size: 11px; line-height: 1.3; color: #666;">
                                <strong style="font-size: 11px;">Límites</strong><br/>
                                Normal: ${limits.normal}<br/>
                                Bajo: ${limits.bajo}<br/>
                                Medio: ${limits.medio}<br/>
                                Alto: ${limits.alto}
                            </div>
                            `
                            : ''
                        }
                    `;
                }
            },
            legend: {
                show: true,
                type: 'scroll',
                orient: 'horizontal',
                bottom: 50,
                left: 'center',
                textStyle: {
                    fontSize: 10
                }
            },
            xAxis: {
                type: 'category',
                data: dataChart.date,
                axisLabel: {
                    interval: 0,
                    rotate: 60,
                    fontSize: 9
                }
            },
            yAxis: {
                type: 'value',
                name: 'cél/mL'
            },
            dataZoom: [
                {
                    type: 'slider',
                    showDataShadow:false,
                },
                {
                    type: 'inside'
                }
            ],
            grid: {
                left: 40,
                right: 20,
                top: 50,
                bottom: 130
            },
            series: seriesForChart
        };

        return (
            <EChartsBarChart
                options={options}
                minHeight={minHeight}
                maxHeight={maxHeight}
            />
        );
    };

    return (
        <Card className='rounded-4 pb-0' style={h100 ? { height: '100%' } : {}}>
            {showHeader && (
                <CardHeader className="p-2 align-items-center d-flex">
                    <i className="ri-bar-chart-fill align-middle me-1 text-muted" style={{ fontSize: 16 }}></i>
                    <h5 className="card-title mb-0 flex-grow-1">{title}</h5>
                    <div className="btn-box-dashboard">
                        {allowedDataIntervals.map(type => (
                            <Button
                                key={type}
                                color={dataInterval === type ? 'secondary' : 'primary'}
                                size="sm"
                                className={`btn-soft-secondary ${dataInterval === type ? 'active' : ''}`}
                                onClick={() => setDataInterval(type)}
                            >
                                {type.toUpperCase()}
                            </Button>
                        ))}
                    </div>
                </CardHeader>
            )}
            <CardBody className="p-0 m-0 px-2">
                <div id="revenue-expenses-charts" dir="ltr" style={{ paddingLeft: '0px', paddingRight: '5px' }}>
                    <Charts/>
                </div>
            </CardBody>
            {showFooter && (
                <CardFooter className='p-0'>
                    <Row className="m-2 p-0">
                        {(data?.summary ? Object.entries(data.summary) : []).map(([key, values = {}], index) => (
                            <Col
                                key={index}
                                md={6}
                                className={`m-0 p-0 ${index !== 0 ? 'border-start' : ''}`}
                            >
                                <h5 className="m-0 text-center" style={{ color: revenueExpensesChartsColors[index] }}>
                                    {key}
                                </h5>
                                <ul className="list-inline main-chart text-center mb-0">
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">
                                            {values.min || '--'}
                                        </h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Mínimo</span>
                                    </li>
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">
                                            {values.avg || '--'}
                                        </h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Promedio</span>
                                    </li>
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">
                                            {values.max || '--'}
                                        </h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Máximo</span>
                                    </li>
                                </ul>
                            </Col>
                        ))}

                        {/* Renderizar un Col vacío si no hay datos */}
                        {(!data?.summary || Object.keys(data.summary).length === 0) && (
                            <Col md={12} className="m-0 p-0">
                                <h5 className="m-0 text-center">Sin datos</h5>
                                <ul className="list-inline main-chart text-center mb-0">
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">--</h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Mínimo</span>
                                    </li>
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">--</h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Promedio</span>
                                    </li>
                                    <li className="list-inline-item chart-border-left p-2 m-0 border-0">
                                        <h5 className="m-0 fs-11">--</h5>
                                        <span className="text-muted d-inline-block fs-10 align-middle">Máximo</span>
                                    </li>
                                </ul>
                            </Col>
                        )}
                    </Row>
                </CardFooter>
            )}
        </Card>
    );
};

export default MicroalgaeConcentrationSummary;